import type {ReactNode} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Tag} from '@/components/ui/tag'
import {AmberDot} from '@/components/amber-dot'
import {AtajoNumero, OPCION_BASE, OPCION_OFF, TiraOpciones} from '@/components/bateria/tira-opciones'
import {VerElPapel} from '@/components/bateria/cola/ver-el-papel'
import {PaginaDelPapel} from '@/components/bateria/cola/pagina-del-papel'
import {ChipDeEstado} from '@/components/bateria/cola/chip-estado'
import {NavegacionTarjeta} from '@/components/bateria/cola/posicion-tarjeta'
import {cn} from '@/lib/utils'
import {SIN_RESPUESTA, cuestionarioForItem, scaleForItem, type ModoRecorte} from '@/lib/bateria'
import {AvisoGesto, type GestoEnCurso} from '@/components/bateria/cola/gesto-en-curso'
import {EsperandoElPapel} from '@/components/bateria/cola/evidencia-de-la-tarjeta'
import {HelpIcon} from '@/components/help-icon'
import {claveDeRef, paqueteDeRef, type SeccionClave, type TarjetaCola} from '@/lib/bateria-cola'
import {useRecorteVivo} from '@/lib/recortes-caidos'
import {useRecorteAlVuelo, type RecorteAlVuelo} from '@/hooks/bateria/use-recorte-al-vuelo'

const TONO_NIVEL: Record<ModoRecorte, 'green' | 'amber'> = {fila: 'green', ventana: 'amber', pagina: 'amber'}

type RecorteDeFila = {
  url: string
  opcionesPct: number[] | null | undefined
  modo: ModoRecorte
  /** Mientras el servidor corta; `undefined` una vez resuelto. */
  buscando?: ReactNode
}

/**
 * Escalera de evidencia: el recorte guardado (vivo), el cortado al vuelo (modo `fila`) o la hoja completa.
 * Mientras se corta se dice, sin pedir la hoja a la vez.
 */
function recorteDeLaFila(
  tarjeta: TarjetaCola,
  recorte: string | null,
  alVuelo: ReturnType<typeof useRecorteAlVuelo>
): RecorteDeFila {
  // Caído el PNG guardado se pinta la hoja: repetir `modo_recorte` alinearía la tira contra casillas ausentes.
  const caido = recorte === null && tarjeta.crop_download_url !== null && tarjeta.crop_download_url !== ''
  const modoGuardado: ModoRecorte = caido ? 'pagina' : ((tarjeta.modo_recorte as ModoRecorte | null) ?? 'ventana')
  if (recorte) {
    return {url: recorte, opcionesPct: tarjeta.opciones_pct, modo: modoGuardado}
  }
  const cortado: RecorteAlVuelo | undefined = alVuelo.data
  if (cortado) return {url: cortado.url, opcionesPct: cortado.opciones_pct, modo: 'fila'}
  return {
    url: '',
    opcionesPct: tarjeta.opciones_pct,
    modo: modoGuardado,
    // `isLoading` y no `isPending`: con la consulta apagada `isPending` queda `true` para siempre.
    buscando: alVuelo.isLoading ? <BuscandoElRecorte /> : undefined,
  }
}

function BuscandoElRecorte() {
  const {t} = useTranslation()
  return (
    <EsperandoElPapel testId="cola-marca-cortando-recorte">
      {t('admin.bateria.cola.papel.cortandoRecorte')}
    </EsperandoElPapel>
  )
}

/**
 * Texto junto al papel, nunca un formulario: la batería solo se aplica en papel o en SIRPSI (Res. 2764/2022).
 * Sin la pregunta en el catálogo no se pinta nada.
 */
function EnunciadoImpreso({enunciado}: {enunciado: string | null}) {
  if (!enunciado) return null
  return (
    <p className="text-sm text-foreground" data-testid="cola-marca-enunciado">
      {enunciado}
    </p>
  )
}

function PieDeMarca({tarjeta, itemId}: {tarjeta: TarjetaCola; itemId: string}) {
  const {t} = useTranslation()
  const match = itemId.match(/^(.*)_(\d+)$/)
  const numero = match ? match[2] : itemId
  const cuestionario = t(`admin.bateria.cuestionario.${cuestionarioForItem(itemId)}`)
  const hoja = tarjeta.pagina_numero ? t('admin.bateria.cola.papel.dondeBuscar', {numero: tarjeta.pagina_numero}) : null
  return (
    <p className="font-mono text-[11px] tracking-wider text-muted-foreground uppercase" data-testid="cola-marca-pie">
      {[tarjeta.persona_corta, cuestionario, numero, hoja].filter(Boolean).join(' · ')}
    </p>
  )
}

/**
 * v2 §1.4. La sospecha va ámbar y punteada y `Enter` no la acepta (§1.5): sería volver dato un «no sé».
 * Una lectura firme no tiene recorte propio: su papel es la hoja del escaneo.
 */
export function TarjetaMarca({
  tarjeta,
  seccion,
  posicion,
  seleccion,
  gesto,
  casillasGuardadas,
  businessId,
  evaluacionId,
  puedeDecidir,
  onElegir,
  onTachonSinRespuesta,
  onDespues,
  onAtras,
  onAdelante,
}: {
  tarjeta: TarjetaCola
  seccion: SeccionClave
  posicion: ReactNode
  seleccion: string | null
  gesto: GestoEnCurso
  /** Aquí apaga los botones; en `evidencia-de-la-tarjeta.tsx` apaga las teclas. */
  puedeDecidir: boolean
  /** Se repintan al volver: sin ellas un tachón resuelto se veía sin casillas. */
  casillasGuardadas: readonly string[]
  businessId: string
  evaluacionId: string
  onElegir: (valor: string | null) => void
  onTachonSinRespuesta: () => void
  onDespues: () => void
  onAtras: () => void
  onAdelante: () => void
}) {
  const {t} = useTranslation()
  const itemId = claveDeRef(tarjeta.ref) ?? tarjeta.ref
  const escala = scaleForItem(itemId)
  const esTachon = tarjeta.flag_marca === 'tachon'
  const guardado = useRecorteVivo(tarjeta.crop_download_url)
  const alVuelo = useRecorteAlVuelo(businessId, evaluacionId, paqueteDeRef(tarjeta.ref), itemId, guardado === null)
  const recorte = recorteDeLaFila(tarjeta, guardado, alVuelo)
  const modo = recorte.modo
  // Mientras se corta no se afirma nivel de recorte ni degradado: todavía puede salir `fila`.
  const cortando = recorte.buscando !== undefined
  const papel =
    recorte.url !== ''
      ? undefined
      : (recorte.buscando ?? <PaginaDelPapel tarjeta={tarjeta} businessId={businessId} evaluacionId={evaluacionId} />)

  return (
    <section className="surface" data-testid="cola-tarjeta-marca">
      <header className="surface-divider flex flex-wrap items-center gap-2 border-b px-5 py-3.5">
        <span className="font-mono text-[11px] tracking-wider text-muted-foreground uppercase">
          {t(`admin.bateria.cola.seccion.${seccion}`)}
        </span>
        <ChipDeEstado tarjeta={tarjeta} />
        {tarjeta.flag_marca && (
          <Tag tone="amber" data-testid="cola-marca-flag">
            {t(`admin.bateria.flag.${tarjeta.flag_marca}`, {defaultValue: tarjeta.flag_marca})}
          </Tag>
        )}
        {!cortando && (
          <Tag tone={TONO_NIVEL[modo]} data-testid="cola-marca-nivel">
            {t(`admin.bateria.revision.nivel.${modo}`)}
          </Tag>
        )}
        {tarjeta.procedencia_fila && tarjeta.procedencia_fila !== 'grid' && (
          <Tag data-testid="cola-marca-procedencia">
            {t(`admin.bateria.cola.procedencia.${tarjeta.procedencia_fila}`, {
              defaultValue: tarjeta.procedencia_fila,
            })}
          </Tag>
        )}
        {posicion}
      </header>

      <div className="space-y-3 px-5 py-4">
        <EnunciadoImpreso enunciado={tarjeta.enunciado} />
        {tarjeta.sospecha && (
          <p
            className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground"
            data-testid="cola-marca-sospecha">
            <AmberDot label={t('common.senal.sinConfirmar')} />
            {t('admin.bateria.cola.marca.sospecha', {valor: t(`admin.bateria.escala.${tarjeta.sospecha}`)})}
            {tarjeta.confianza != null && (
              <span className="font-mono text-[11px] tabular-nums" data-testid="cola-marca-confianza">
                {t('admin.bateria.cola.marca.confianza', {pct: Math.round(tarjeta.confianza * 100)})}
              </span>
            )}
          </p>
        )}
        {/* `sospecha_tinta` solo existe sin lectura de casilla: es una pista de tinta, nunca una lectura, y no lleva confianza. */}
        {!tarjeta.sospecha && tarjeta.sospecha_tinta && (
          <p
            className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground"
            data-testid="cola-marca-sospecha-tinta">
            <AmberDot label={t('common.senal.sinConfirmar')} />
            {t('admin.bateria.cola.marca.sospechaTinta', {valor: t(`admin.bateria.escala.${tarjeta.sospecha_tinta}`)})}
          </p>
        )}
        <TiraOpciones
          cropUrl={recorte.url}
          opciones={escala.map((valor, i) => ({
            valor,
            etiqueta: t(`admin.bateria.escala.${valor}`),
            pct: recorte.opcionesPct?.[i] ?? 0,
          }))}
          seleccion={seleccion}
          onElegir={onElegir}
          modo={modo}
          marcadas={gesto?.casillas ?? casillasGuardadas}
          propuesta={tarjeta.sospecha ?? tarjeta.sospecha_tinta}
          propuestaEtiqueta={
            tarjeta.sospecha
              ? t('admin.bateria.cola.marca.propuestaLeida')
              : t('admin.bateria.cola.marca.propuestaTinta')
          }
          deshabilitada={!puedeDecidir}
          evidenciaEnCurso={cortando}
          papel={papel}
          extra={
            esTachon && puedeDecidir ? (
              <button
                type="button"
                data-testid="cola-marca-tachon-sin-respuesta"
                aria-keyshortcuts="X"
                onClick={onTachonSinRespuesta}
                className={cn(
                  OPCION_BASE,
                  OPCION_OFF,
                  'inline-flex min-h-11 items-center gap-1 border-dashed md:min-h-0'
                )}>
                <span aria-hidden="true" className="font-mono opacity-60">
                  X
                </span>
                {t('admin.bateria.cola.marca.tachonSinRespuesta')}
              </button>
            ) : undefined
          }
        />
        {esTachon && !gesto && (
          <p
            className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground"
            data-testid="cola-marca-tachon-ayuda">
            <AtajoNumero n={1} />
            {t('admin.bateria.cola.marca.tachonAyuda.valida', {max: escala.length})}
            <span aria-hidden="true" className="font-mono opacity-60">
              X
            </span>
            {t('admin.bateria.cola.marca.tachonAyuda.ninguna')}
            <span aria-hidden="true" className="font-mono opacity-60">
              T
            </span>
            {t('admin.bateria.cola.marca.tachonAyuda.senalar')}
          </p>
        )}
        {/* Nadie pide esta tarjeta: se dice, o se vería idéntica a una que sí se necesita. */}
        {tarjeta.no_aplica_filtro === true && (
          <p className="flex items-center gap-1.5" data-testid="cola-marca-no-aplica-filtro">
            <Tag tone="neutral">{t('admin.bateria.cola.marca.noAplicaFiltroChip')}</Tag>
            <HelpIcon
              label={t('common.help.iconLabelFor', {campo: t('admin.bateria.cola.marca.noAplicaFiltroChip')})}
              testId="cola-marca-no-aplica-filtro-tip">
              {t('admin.bateria.cola.marca.noAplicaFiltroTip')}
            </HelpIcon>
          </p>
        )}
        {!gesto && (
          <p
            className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground"
            data-testid="cola-marca-ayuda">
            <span aria-hidden="true" className="font-mono opacity-60">
              Enter
            </span>
            {t('admin.bateria.cola.marca.ayuda.enterNoAcepta')}
            <span aria-hidden="true" className="font-mono opacity-60">
              M
            </span>
            {t('admin.bateria.cola.marca.ayuda.varias')}
          </p>
        )}
        {gesto && (
          <AvisoGesto
            testId="cola-marca-gesto"
            mensaje={t(
              gesto.tipo === 'tachon' && !gesto.senalando
                ? 'admin.bateria.cola.marca.modo.tachonValida'
                : `admin.bateria.cola.marca.modo.${gesto.tipo}`,
              {
                casillas:
                  gesto.casillas.map((valor) => t(`admin.bateria.escala.${valor}`)).join(', ') ||
                  t('admin.bateria.cola.marca.ninguna'),
              }
            )}
          />
        )}
        <VerElPapel tarjeta={tarjeta} testIdPrefix="cola-marca" />
        <PieDeMarca tarjeta={tarjeta} itemId={itemId} />
      </div>

      <div className="surface-divider flex flex-wrap items-center justify-between gap-2 border-t px-5 py-3">
        <NavegacionTarjeta onAtras={onAtras} onAdelante={onAdelante} />
        {seleccion === SIN_RESPUESTA && (
          <span className="font-mono text-[11px] text-muted-foreground" data-testid="cola-marca-en-blanco">
            {t('admin.bateria.tiraOpciones.enBlanco')}
          </span>
        )}
        <Button
          size="sm"
          variant="ghost"
          className="min-h-11 md:min-h-0"
          data-testid="cola-despues"
          onClick={onDespues}>
          {t('admin.bateria.cola.despues')}
        </Button>
      </div>
    </section>
  )
}
